import React, { useState } from "react";
import { Modal, Steps, Button, Form, Select, Space, message } from "antd";

const { Step } = Steps;

// ================= INTERFACES =================
export interface MatchConfig {
  id: number;
  matchesCode: number;
  team1: string | null; // 🟢 ID bảng
  rank1: number | null; // 🟢 Hạng đội (1=Nhất, 2=Nhì, 3=Ba)
  team2: string | null; // 🟢 ID bảng
  rank2: number | null;
}

export interface RoundConfig {
  totalTeams: number;
  matches: MatchConfig[];
}

export interface GroupInfo {
  id: string;   // TournamentGroupID
  group: string; // ví dụ "A", "B", "C"
}

interface KnockoutSetupPopupProps {
  open: boolean;
  tournamentID: string;
  onClose: () => void;
  groups: GroupInfo[];
  onSave: (data: RoundConfig) => void;
}

// ================= COMPONENT =================
const KnockoutSetupPopup: React.FC<KnockoutSetupPopupProps> = ({
  open,
  tournamentID,
  onClose,
  groups,
  onSave,
}) => {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const [roundConfig, setRoundConfig] = useState<RoundConfig | null>(null);
  const [isOpenPrevStep, setIsOpenPrevStep] = useState(false);

  const next = () => setCurrent((prev) => prev + 1);
  const prev = () => setIsOpenPrevStep(true);
  const handleClosePrevStep = () => setIsOpenPrevStep(false);
  const handleConfirmPrevStep = () => {
    setCurrent((prev) => prev - 1);
    setIsOpenPrevStep(false);
  };



  const selectedValues = roundConfig?.matches.flatMap((m) => {
    const arr: string[] = [];
    if (m.team1 && m.rank1) {
      arr.push(JSON.stringify({ groupId: m.team1, rank: m.rank1 }));
    }
    if (m.team2 && m.rank2) {
      arr.push(JSON.stringify({ groupId: m.team2, rank: m.rank2 }));
    }
    return arr;
  }) ?? [];

  const getFilteredOptions = (currentValue?: string) => {
    return generateTeamOptions(groups).filter((opt) => {
      // giữ lại option đang được chọn
      if (opt.value === currentValue) return true;

      // loại bỏ option đã được chọn ở select khác
      return !selectedValues.includes(opt.value);
    });
  };


  // 🟢 Bước 1: Chọn số đội
  const Step1 = (
    <Form form={form} layout="vertical">
      <Form.Item
        name="totalTeams"
        label="Số đội tham gia vòng này"
        rules={[{ required: true, message: "Vui lòng chọn số đội (8,16,32...)" }]}
      >
        <Select placeholder="Chọn số đội tham gia" allowClear style={{ width: "100%" }}>
          {[2, 4, 8, 16, 32, 64, 128].map((teamCount) => (
            <Select.Option key={teamCount} value={teamCount}>
              {teamCount} đội
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Button
        type="primary"
        onClick={() => {
          form.validateFields().then((values) => {
            const totalTeams = values.totalTeams;
            const totalMatches = totalTeams / 2;

            const matches: MatchConfig[] = Array.from({ length: totalMatches }, (_, i) => ({
              id: i + 1,
              matchesCode: i + 1,
              team1: null,
              team2: null,
              rank1: null,
              rank2: null,
            }));

            setRoundConfig({ totalTeams, matches });
            next();
          });
        }}
      >
        Tạo các trận
      </Button>
    </Form>
  );

  // 🟢 Bước 2: Chọn đội cho từng trận
  const Step2 = (
    <>
      {roundConfig?.matches.map((match, index) => (
        <Space key={match.id} style={{ display: "flex", marginBottom: 12 }}>
          <b>Trận M{index + 1}</b>
          {/* Đội 1 */}
          {/* <Select
            placeholder="Chọn đội 1"
            options={generateTeamOptions(groups)}
            style={{ width: 220 }}
            value={
              match.team1
                ? JSON.stringify({ groupId: match.team1, rank: match.rank1 })
                : undefined
            }
            onChange={(val) => updateTeam(index, "team1", val)}
          /> */}
          <Select
            placeholder="Chọn đội 1"
            style={{ width: 220 }}
            options={getFilteredOptions(
              match.team1
                ? JSON.stringify({ groupId: match.team1, rank: match.rank1 })
                : undefined
            )}
            value={
              match.team1
                ? JSON.stringify({ groupId: match.team1, rank: match.rank1 })
                : undefined
            }
            onChange={(val) => updateTeam(index, "team1", val)}
          />

          <span>VS</span>
          {/* Đội 2 */}
          {/* <Select
            placeholder="Chọn đội 2"
            options={generateTeamOptions(groups)}
            style={{ width: 220 }}
            value={
              match.team2
                ? JSON.stringify({ groupId: match.team2, rank: match.rank2 })
                : undefined
            }
            onChange={(val) => updateTeam(index, "team2", val)}
          /> */}
          <Select
            placeholder="Chọn đội 2"
            style={{ width: 220 }}
            options={getFilteredOptions(
              match.team2
                ? JSON.stringify({ groupId: match.team2, rank: match.rank2 })
                : undefined
            )}
            value={
              match.team2
                ? JSON.stringify({ groupId: match.team2, rank: match.rank2 })
                : undefined
            }
            onChange={(val) => updateTeam(index, "team2", val)}
          />

        </Space>
      ))}
      <div style={{ marginTop: 16 }}>
        <Button onClick={prev} style={{ marginRight: 8 }}>
          Quay lại
        </Button>
        <Button
          type="primary"
          onClick={() => {
            if (roundConfig!.matches.some((m) => !m.team1 || !m.team2)) {
              message.error("Vui lòng chọn đủ đội cho tất cả các trận!");
              return;
            }
            next();
          }}
        >
          Tiếp tục
        </Button>
      </div>
    </>
  );

  // 🟢 Bước 3: Xác nhận & lưu
  // 🟢 Bước 3: Xác nhận & lưu
  const Step3 = (
    <>
      <h4>Xác nhận cấu hình</h4>
      <ul>
        {roundConfig?.matches.map((m) => {
          const group1 = groups.find((g) => g.id === m.team1);
          const group2 = groups.find((g) => g.id === m.team2);

          return (
            <li key={m.id}>
              Trận M{m.id}:{" "}
              {getRankLabel(m.rank1)} {group1 ? group1.group : "?"} vs{" "}
              {getRankLabel(m.rank2)} {group2 ? group2.group : "?"}
            </li>
          );
        })}
      </ul>
      <div style={{ marginTop: 16 }}>
        <Button onClick={prev} style={{ marginRight: 8 }}>
          Quay lại
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onSave(roundConfig!);
            message.success("Đã lưu cấu hình vòng đấu!");
            onClose();
          }}
        >
          Lưu lại
        </Button>
      </div>
    </>
  );


  // 🟢 Cập nhật đội cho từng trận
  const updateTeam = (
    matchIndex: number,
    field: "team1" | "team2",
    value: string
  ) => {
    const parsed = JSON.parse(value); // { groupId, rank }
    setRoundConfig((prev) => {
      if (!prev) return prev;
      const updated = [...prev.matches];
      const match = { ...updated[matchIndex] };

      if (field === "team1") {
        match.team1 = parsed.groupId;
        match.rank1 = parsed.rank;
      } else {
        match.team2 = parsed.groupId;
        match.rank2 = parsed.rank;
      }

      updated[matchIndex] = match;
      return { ...prev, matches: updated };
    });
  };

  // 🟢 Các bước hiển thị
  const steps = [
    { title: "Chọn số đội", content: Step1 },
    { title: "Chọn đội cho từng trận", content: Step2 },
    { title: "Xác nhận & lưu", content: Step3 },
  ];

  return (
    <div>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title="Cấu hình vòng đấu loại trực tiếp"
        width={950}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <Steps current={current} size="small" style={{ marginBottom: 24 }}>
          {steps.map((item) => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
        <div>{steps[current].content}</div>
      </Modal>

      <Modal
        open={isOpenPrevStep}
        title="Xác nhận quay lại"
        onCancel={handleClosePrevStep}
        onOk={handleConfirmPrevStep}
        okText="Đồng ý"
        okButtonProps={{ danger: true }}
        cancelText="Hủy"
      >
        <p>Khi xác nhận quay lại dữ liệu bạn tạo trước đó sẽ mất. Bạn chắc chắn ?</p>
      </Modal>
    </div>
  );
};

// 🟢 Sinh danh sách chọn (Nhất/Nhì/Ba cho từng bảng)
const generateTeamOptions = (groups: GroupInfo[]) => {
  const ranks = [
    { label: "Nhất", value: 1 },
    { label: "Nhì", value: 2 },
    { label: "Ba", value: 3 },
  ];

  return groups.flatMap((g) =>
    ranks.map((r) => ({
      label: `${r.label} ${g.group}`,
      value: JSON.stringify({ groupId: g.id, rank: r.value }),
    }))
  );
};

// 🟢 Helper để hiển thị hạng
const getRankLabel = (rank: number | null) => {
  if (rank === 1) return "Nhất";
  if (rank === 2) return "Nhì";
  if (rank === 3) return "Ba";
  return "";
};

export default KnockoutSetupPopup;
